import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useNotification } from "../common/NotificationContext";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMars, faVenus } from "@fortawesome/free-solid-svg-icons";
import { Button } from "@mmfestival/shared-components";

// Fungsi untuk mengkapitalkan huruf pertama setiap kata
const capitalizeWords = (str) => {
  if (!str || typeof str !== "string") return "";
  return str
    .trim()
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Fungsi untuk mengonversi waktu HH:MM:SS ke detik untuk sorting numerik
const timeToSeconds = (time) => {
  if (!time) return Infinity;
  const [h, m, s] = time.split(":").map(Number);
  return h * 3600 + m * 60 + s;
};

// Fungsi untuk mengonversi kode CIO atau nama negara ke kode ISO untuk bendera
const getFlagCode = (nationality) => {
  if (!nationality) return "un";
  const codeMap = {
    INA: "id",
    INDONESIA: "id",
    GERMANY: "de",
    RUSSIA: "ru",
    FRANCE: "fr",
    USA: "us",
    GBR: "gb",
    MALAYSIA: "my",
    MY: "my",
    JAPAN: "jp",
    JPN: "jp",
    "SOUTH KOREA": "kr",
    KOR: "kr",
    KOREA: "kr",
    THAILAND: "th",
    THA: "th",
    ITALY: "it",
    ITA: "it",
    SINGAPORE: "sg",
    SGP: "sg",
    AUSTRALIA: "au",
    AUS: "au",
    CHINA: "cn",
    CHN: "cn",
    BRUNEI: "bn",
    BRU: "bn",
    PHILIPPINES: "ph",
    PHI: "ph",
    SPAIN: "es",
    ESP: "es",
    NETHERLANDS: "nl",
    NED: "nl",
    SWITZERLAND: "ch",
    SUI: "ch",
    SWEDEN: "se",
    SWE: "se",
    BELGIUM: "be",
    BEL: "be",
    INDIA: "in",
    IND: "in",
    "UNITED KINGDOM": "gb",
    UK: "gb",
    NORWAY: "no",
    NOR: "no",
    EGYPT: "eg",
    EGY: "eg",
    CZECH: "cz",
    "CZECH REPUBLIC": "cz",
    CZE: "cz",
    MOROCCO: "ma",
    MAR: "ma",
    "SINT MAARTEN (DUTCH PART)": "sx",
    SXM: "sx",
    TAIWAN: "tw",
    TWN: "tw",
    BELARUS: "by",
    BLR: "by",
  };
  return codeMap[nationality.toUpperCase()] || "un";
};

const slugifyDistance = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const getCanonicalSmmDistance = (categoryName, rawDistance) => {
  const categorySlug = slugifyDistance(categoryName);
  const distanceSlug = slugifyDistance(rawDistance);

  if (categorySlug === "7-km" || distanceSlug === "7") return "7";
  if (categorySlug === "half-marathon" || distanceSlug === "21" || distanceSlug === "21-9") return "21";
  if (categorySlug === "marathon" || distanceSlug === "42") return "42";
  if (categorySlug === "kaldera-kids-run") return "kaldera-kids-run";
  if (categorySlug === "porter-challenge") return "porter-challenge";

  return distanceSlug || categorySlug;
};

// Fungsi untuk mengonversi gender ke format dengan ikon
const formatGender = (gender, isMixGroup) => {
  if (isMixGroup) {
    return (
      <>
        <FontAwesomeIcon icon={faMars} className="mr-1" />
        <FontAwesomeIcon icon={faVenus} className="mr-1" />
        MIX
      </>
    );
  }
  if (!gender) return "-";
  if (gender === "M" || gender === "Laki-laki")
    return (
      <>
        <FontAwesomeIcon icon={faMars} className="mr-1" /> Male
      </>
    );
  if (gender === "F" || gender === "Perempuan")
    return (
      <>
        <FontAwesomeIcon icon={faVenus} className="mr-1" /> Female
      </>
    );
  return gender;
};

// Komponen untuk baris skeleton saat loading
const SkeletonRow = React.memo(() => (
  <tr className="border-b border-gray-200">
    {Array(7)
      .fill()
      .map((_, index) => (
        <td key={index} className="px-4 py-4">
          <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
        </td>
      ))}
  </tr>
));

// Komponen untuk tombol kategori
const CategoryButton = React.memo(({ category, selectedCategory, onClick }) => (
  <button
    onClick={() => onClick(category.distance)}
    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
      selectedCategory === category.distance
        ? "bg-slate-700 text-white"
        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
    }`}
    aria-label={`Filter hasil berdasarkan kategori ${category.displayName}`}
  >
    {category.displayName}
  </button>
));

// Komponen untuk tabel top 3
const Top3Table = ({ title, results, isMixGroup }) => (
  <div className="w-full md:w-[calc(50%-0.5rem)] bg-white rounded-xl border border-gray-300 overflow-x-auto">
    <h2 className="text-lg font-semibold text-slate-800 mb-4 px-4 pt-4">
      {title}
    </h2>

    <table className="min-w-full table-auto border-collapse">
      <thead className="bg-gray-50 border-y border-gray-300">
        <tr>
          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[60px]">
            Rank
          </th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Time
          </th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[150px]">
            Name
          </th>
        </tr>
      </thead>

      <tbody className="bg-white divide-y divide-gray-200">
        {results.length === 0 ? (
          <tr>
            <td
              colSpan={3}
              className="px-4 py-2 text-center text-sm text-gray-600"
            >
              No data available
            </td>
          </tr>
        ) : (
          results.map((result, index) => (
            <tr
              key={`top3-${result.id || index}`}
              className="hover:bg-gray-50 transition-colors"
            >
              <td className="px-2 py-2 text-sm text-gray-600 text-center w-[50px]">
                {result.genderRanking || result.displayRanking}
              </td>
              <td className="px-4 py-2 text-sm text-gray-600 font-bold w-[100px]">
                {result.time || "-"}
              </td>
              <td className="px-4 py-2 text-sm text-gray-600 uppercase max-w-[150px] truncate">
                {isMixGroup
                  ? capitalizeWords(result.teamName)?.toUpperCase() ||
                    "UNKNOWN TEAM"
                  : capitalizeWords(
                      `${result.firstName} ${result.familyName || ""}`
                    )?.toUpperCase() || "-"}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

export const Results = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const selectedEvent = (eventId || "").toUpperCase();
  const [state, setState] = useState({
    raceResults: [],
    top3Male: [],
    top3Female: [],
    loading: true,
    totalResults: 0,
    selectedCategory: "",
    categories: [],
    categoriesLoaded: false,
    bibNumberCache: new Map(),
    events: [],
    loadingEvents: true,
  });

  const db = getFirestore();
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const eventsRef = collection(db, "semuaKegiatan");
        const snapshot = await getDocs(eventsRef);
        const events = snapshot.docs
          .map((eventDoc) => ({
            id: eventDoc.id,
            ...eventDoc.data(),
          }))
          .sort((a, b) => String(a.title || a.id).localeCompare(String(b.title || b.id)));

        if (!isMounted.current) return;

        setState((prev) => ({
          ...prev,
          events,
          loadingEvents: false,
        }));

        if (!selectedEvent && events.length > 0) {
          navigate(`/results/${events[0].id}`, { replace: true });
        }
      } catch (err) {
        console.error("[ERROR] Failed to fetch events:", err);
        if (isMounted.current) {
          setState((prev) => ({ ...prev, loadingEvents: false }));
        }
      }
    };

    fetchEvents();
  }, [db, navigate, selectedEvent]);

  const fetchAllBibNumbers = async () => {
    if (!selectedEvent) return new Map();
    try {
      const participantsCollection = collection(db, `pendaftaran${selectedEvent}`);
      const participantsSnapshot = await getDocs(participantsCollection);
      const bibNumberCache = new Map();
      participantsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.kategori && String(data.kategori).toLowerCase().includes("mix")) {
          const bibNumbers = [
            data.bibNumber || "-",
            ...(data.participants || [])
              .slice(0, 2)
              .map((p) => p.bibNumber || "-"),
          ]
            .slice(0, 3)
            .join(", ");
          bibNumberCache.set(doc.id, {
            bibNumber: bibNumbers || "-",
            teamName: capitalizeWords(data.teamName) || "-",
            nationality:
              data.participants?.[0]?.negara || data.negara || "Indonesia",
          });
        } else {
          bibNumberCache.set(doc.id, {
            bibNumber: data.bibNumber || "-",
            teamName: null,
            nationality: data.negara || "Indonesia",
          });
        }
      });
      return bibNumberCache;
    } catch (err) {
      console.error("[ERROR] Failed to fetch bibNumbers:", err);
      showNotification("error", "Failed to load bib numbers.");
      return new Map();
    }
  };

  const fetchCategories = useCallback(async () => {
    if (!selectedEvent) return;
    if (selectedEvent === "JMM") return;
    try {
      const eventRef = doc(db, "semuaKegiatan", selectedEvent);
      const eventDoc = await getDoc(eventRef);
      let mappedCategories = [];

      if (eventDoc.exists()) {
        const rawCategories = eventDoc.data().categories || [];
        const seenDistances = new Set();

        rawCategories.forEach((cat) => {
          const rawDistance =
            cat.distance != null && String(cat.distance).trim() !== ""
              ? String(cat.distance).trim()
              : String(cat.categoryName || "").trim();

          let distance = slugifyDistance(rawDistance);

          if (selectedEvent === "SMM") {
            distance = getCanonicalSmmDistance(cat.categoryName, rawDistance);
          }

          if (
            selectedEvent === "SMM" &&
            ["kaldera-kids-run", "porter-challenge"].includes(distance)
          ) {
            return;
          }

          let displayName = cat.categoryName;
          let categoryName = cat.categoryName;
          let description = cat.description || cat.categoryName;

          const startDateTime = cat.eventDateTimestamp
            ? String(cat.eventDateTimestamp).replace(/\s*UTC.*$/, "")
            : [cat.date, cat.time].filter(Boolean).join(" ") || "-";

          if (!seenDistances.has(distance)) {
            seenDistances.add(distance);
            mappedCategories.push({
              distance,
              displayName,
              description,
              categoryName,
              startDateTime,
            });
          }
        });
      }

      // Check if we need fallback for SMM (if doc doesn't exist OR categories are empty)
      if (selectedEvent === "SMM" && mappedCategories.length === 0) {
        mappedCategories = [
          {
            distance: "42",
            displayName: "SMM 42K",
            description: "SMM Marathon - 42K",
            categoryName: "Marathon",
            startDateTime: "17 May 2025 03:30",
          },
          {
            distance: "21",
            displayName: "SMM 21K",
            description: "SMM Half Marathon - 21K",
            categoryName: "Half Marathon",
            startDateTime: "17 May 2025 06:00",
          },
          {
            distance: "7",
            displayName: "SMM 7K",
            description: "SMM 7 KM",
            categoryName: "7 KM",
            startDateTime: "17 May 2025 06:30",
          },
        ];
      }

      if (mappedCategories.length === 0 && selectedEvent !== "SMM") {
        showNotification(
          "error",
          `${selectedEvent} document or categories not found.`
        );
      }

      if (isMounted.current) {
        setState((prev) => ({
          ...prev,
          categories: mappedCategories,
          categoriesLoaded: true,
          selectedCategory: mappedCategories.some(
            (cat) => cat.distance === prev.selectedCategory
          )
            ? prev.selectedCategory
            : mappedCategories[0]?.distance || "",
        }));
      }
    } catch (err) {
      console.error("[ERROR] Failed to fetch categories:", err);
      // Last-ditch SMM fallback if everything else fails
      if (selectedEvent === "SMM") {
        const fallback = [
          {
            distance: "42",
            displayName: "SMM 42K",
            description: "SMM Marathon - 42K",
            categoryName: "Marathon",
            startDateTime: "17 May 2025 03:30",
          },
          {
            distance: "21",
            displayName: "SMM 21K",
            description: "SMM Half Marathon - 21K",
            categoryName: "Half Marathon",
            startDateTime: "17 May 2025 06:00",
          },
          {
            distance: "7",
            displayName: "SMM 7K",
            description: "SMM 7 KM",
            categoryName: "7 KM",
            startDateTime: "17 May 2025 06:30",
          },
        ];
        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            categories: fallback,
            categoriesLoaded: true,
            selectedCategory: fallback[0]?.distance || "",
          }));
        }
      } else {
        showNotification("error", "Failed to load categories.");
      }
    }
  }, [showNotification, selectedEvent]);

  const loadRaceData = useCallback(
    async (selectedDistance = state.selectedCategory) => {
      if (!state.categoriesLoaded || state.categories.length === 0) {
        console.warn("Categories not loaded yet, skipping loadRaceData");
        return;
      }

      setState((prev) => ({ ...prev, loading: true }));
      try {
        const bibNumberCache = await fetchAllBibNumbers();
        const subCollectionRef = collection(
          db,
          `results${selectedEvent}`,
          selectedDistance,
          "results"
        );
        const resultsSnapshot = await getDocs(subCollectionRef);
        const category = state.categories.find(
          (cat) => cat.distance === selectedDistance
        );

        if (!category) {
          console.error(`Category not found for distance: ${selectedDistance}`);
          showNotification("error", "Category not found.");
          setState((prev) => ({ ...prev, loading: false }));
          return;
        }

        let resultsList = resultsSnapshot.docs.map((doc) => {
          const data = doc.data();
          const bibData = bibNumberCache.get(data.orderId) || {
            bibNumber: "-",
            teamName: data.teamName ? capitalizeWords(data.teamName) : null,
            nationality: data.nationality || "Indonesia",
          };
          return {
            id: doc.id,
            ...data,
            categoryName: category.categoryName,
            bibNumber: bibData.bibNumber !== "-" ? bibData.bibNumber : (data.bibNumber || "-"),
            teamName: bibData.teamName || data.teamName,
            nationality: bibData.nationality || data.nationality || "Indonesia",
          };
        });

        const isMixGroup =
          category?.categoryName?.toLowerCase().includes("mix") ||
          String(selectedDistance).includes("mix");

        // Calculate gender rankings and overall rankings
        if (!isMixGroup) {
          const maleResults = resultsList
            .filter(
              (result) =>
                result.gender === "M" && result.ranking !== "DNF" && result.time
            )
            .sort((a, b) => timeToSeconds(a.time) - timeToSeconds(b.time))
            .map((result, index) => ({
              ...result,
              genderRanking: index + 1,
            }));

          const femaleResults = resultsList
            .filter(
              (result) =>
                result.gender === "F" && result.ranking !== "DNF" && result.time
            )
            .sort((a, b) => timeToSeconds(a.time) - timeToSeconds(b.time))
            .map((result, index) => ({
              ...result,
              genderRanking: index + 1,
            }));

          // Combine and sort by overall ranking
          resultsList = [
            ...maleResults,
            ...femaleResults,
            ...resultsList
              .filter(
                (result) =>
                  result.ranking === "DNF" || !result.time || !result.gender
              )
              .map((result) => ({
                ...result,
                genderRanking: "-",
              })),
          ].sort((a, b) => {
            const timeA = timeToSeconds(a.time);
            const timeB = timeToSeconds(b.time);
            
            if (a.ranking === "DNF" || isNaN(timeA)) return 1;
            if (b.ranking === "DNF" || isNaN(timeB)) return -1;
            
            return timeA - timeB;
          });

          // Assign displayRanking based on sorted order
          resultsList = resultsList.map((result, index) => ({
            ...result,
            displayRanking: result.ranking === "DNF" ? "DNF" : index + 1,
          }));
        } else {
          // For mix group, assign displayRanking and no gender ranking
          resultsList = resultsList
            .filter((result) => result.time && result.ranking !== "DNF")
            .sort((a, b) => timeToSeconds(a.time) - timeToSeconds(b.time))
            .map((result, index) => ({
              ...result,
              displayRanking: index + 1,
              genderRanking: "-",
            }))
            .concat(
              resultsList
                .filter((result) => !result.time || result.ranking === "DNF")
                .map((result) => ({
                  ...result,
                  displayRanking: "DNF",
                  genderRanking: "-",
                }))
            );
        }

        // Extract top 3 for non-mix categories
        let top3Male = [];
        let top3Female = [];
        if (!isMixGroup) {
          top3Male = resultsList
            .filter(
              (result) =>
                result.gender === "M" && result.ranking !== "DNF" && result.time
            )
            .slice(0, 3)
            .map((result) => ({
              ...result,
              genderRanking: result.genderRanking,
            }));

          top3Female = resultsList
            .filter(
              (result) =>
                result.gender === "F" && result.ranking !== "DNF" && result.time
            )
            .slice(0, 3)
            .map((result) => ({
              ...result,
              genderRanking: result.genderRanking,
            }));
        }

        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            raceResults: resultsList,
            totalResults: resultsSnapshot.size,
            top3Male,
            top3Female,
            loading: false,
            bibNumberCache,
          }));
        }
      } catch (err) {
        console.error("[ERROR] Error fetching race data:", err);
        if (isMounted.current) {
          setState((prev) => ({ ...prev, loading: false }));
          showNotification(
            "error",
            "Failed to load race results. Please try again."
          );
        }
      }
    },
    [
      showNotification,
      state.selectedCategory,
      state.categories,
      state.categoriesLoaded,
      selectedEvent,
    ]
  );

  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const handleCategoryChange = useCallback(
    debounce((distance) => {
      if (distance === state.selectedCategory) return;

      if (
        !state.categoriesLoaded ||
        !state.categories.some((cat) => cat.distance === distance)
      ) {
        console.warn(`Invalid or unloaded category: ${distance}`);
        showNotification("error", "Category not loaded or invalid.");
        return;
      }

      setState((prev) => ({
        ...prev,
        selectedCategory: distance,
        raceResults: [],
        totalResults: 0,
        loading: true,
        top3Male: [],
        top3Female: [],
      }));
      loadRaceData(distance);
    }, 300),
    [
      state.selectedCategory,
      state.categories,
      state.categoriesLoaded,
      showNotification,
    ]
  );

  const handleNameClick = (result) => {
    const selectedCategoryMeta = state.categories.find(
      (cat) => cat.distance === state.selectedCategory
    );
    const isMixGroup =
      selectedCategoryMeta?.categoryName?.toLowerCase().includes("mix") ||
      state.selectedCategory.includes("mix");

    const name =
      isMixGroup
        ? capitalizeWords(result.teamName)?.toUpperCase() || "-"
        : capitalizeWords(
            `${result.firstName} ${result.familyName || ""}`
          )?.toUpperCase() || "-";

    navigate(
      `/results/${selectedEvent}/${state.selectedCategory}/${encodeURIComponent(
        name
      )}`,
      {
      state: {
        eventId: selectedEvent,
        name,
        ranking: result.displayRanking || "-", // Use displayRanking for overall rank
        category: state.selectedCategory,
        categoryDisplay:
          selectedCategoryMeta?.displayName || result.categoryName || "-",
        startDateTime: selectedCategoryMeta?.startDateTime || "-",
        bibNumber: result.bibNumber || "-",
        nationality: result.nationality || "Indonesia",
        gender: result.gender || "-",
        participants: result.participants || [],
        status: result.time ? "Finished" : "DNF",
        time: result.time || "-",
        genderRanking: result.genderRanking || "-",
      },
    }
    );
  };

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (selectedEvent && state.categoriesLoaded && state.categories.length > 0) {
      loadRaceData(state.selectedCategory);
    }
  }, [
    selectedEvent,
    state.categoriesLoaded,
    state.categories,
    state.selectedCategory,
    loadRaceData,
  ]);

  const handleEventChange = (nextEventId) => {
    if (!nextEventId || nextEventId === selectedEvent) return;
    navigate(`/results/${nextEventId}`);
    setState((prev) => ({
      ...prev,
      raceResults: [],
      top3Male: [],
      top3Female: [],
      totalResults: 0,
      categories: [],
      categoriesLoaded: false,
      selectedCategory: "",
      loading: true,
    }));
  };

  if (selectedEvent === "JMM") {
    return (
      <section className="min-h-screen py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-6">
            <h1 className="text-2xl font-bold text-slate-800">
              JMM 2026 Race Results
            </h1>
         
          </div>
       
          <div className="w-full rounded-xl overflow-hidden border border-gray-200 bg-white">
            <iframe
              src="https://my.raceresult.com/395147/results"
              title="JMM 2026 Live Results"
              className="w-full"
              style={{ height: "calc(100vh - 220px)", minHeight: 600 }}
              loading="lazy"
            />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen py-20 bg-white">
      <div className="container mx-auto px-4">
        <h1 className="text-2xl font-bold text-slate-800 mb-6">
          {selectedEvent ? `${selectedEvent} Race Results` : "Race Results"}
        </h1>

        {state.categoriesLoaded && state.categories.length === 0 && (
          <p className="mb-4 text-sm text-gray-600">
            No categories found for {selectedEvent}.
          </p>
        )}

        {(() => {
          const selectedCategoryMeta = state.categories.find(
            (cat) => cat.distance === state.selectedCategory
          );
          const isSelectedCategoryMix =
            selectedCategoryMeta?.categoryName
              ?.toLowerCase()
              .includes("mix") || state.selectedCategory.includes("mix");

          return (
            <>

        <div className="mb-6 flex flex-wrap gap-2">
          {state.categories.map((category) => (
            <CategoryButton
              key={category.distance}
              category={category}
              selectedCategory={state.selectedCategory}
              onClick={handleCategoryChange}
            />
          ))}
        </div>

        {!isSelectedCategoryMix && (
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <Top3Table
              title="Top 3 Men"
              results={state.top3Male}
              isMixGroup={false}
            />
            <Top3Table
              title="Top 3 Women"
              results={state.top3Female}
              isMixGroup={false}
            />
          </div>
        )}

        {/* Mobile card list */}
        <div className="sm:hidden flex flex-col gap-2">
          {state.loading ? (
            <div className="bg-white rounded-xl border border-gray-300 p-4 text-center text-sm text-gray-500">
              Loading…
            </div>
          ) : state.raceResults.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-300 p-4 text-center text-sm text-gray-600">
              No race results available for this category.
            </div>
          ) : (
            state.raceResults.map((result, index) => {
              const displayName = isSelectedCategoryMix
                ? capitalizeWords(result.teamName)?.toUpperCase() || "-"
                : capitalizeWords(`${result.firstName} ${result.familyName || ""}`)?.toUpperCase() || "-";
              const genderChar = String(result.gender || "").toUpperCase();
              const genderSymbol = genderChar === "M" ? "♂" : genderChar === "F" ? "♀" : "";
              const flagCode = selectedEvent !== "SMM" ? getFlagCode(result.nationality) : null;
              return (
                <div
                  key={`m-${result.id || "u"}-${index}`}
                  className="bg-white rounded-xl border border-gray-300 overflow-hidden"
                  onClick={() => handleNameClick(result)}
                >
                  {/* Row 1: rank + name + time */}
                  <div className="flex items-center gap-3 px-3 py-3">
                    <span className="flex-none w-7 h-7 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold flex items-center justify-center">
                      {result.displayRanking || "-"}
                    </span>
                    <span className="flex-1 min-w-0 text-[13px] font-bold text-gray-900 uppercase truncate">
                      {displayName}
                    </span>
                    <span className="flex-none text-[13px] text-gray-700 tabular-nums">
                      {result.time || "-"}
                    </span>
                  </div>
                  {/* Divider */}
                  <div className="border-t border-gray-200" />
                  {/* Row 2: flag + bib + gender + gender rank */}
                  <div className="flex items-center gap-2 px-3 py-2.5 text-[12px] text-gray-600">
                    {flagCode ? (
                      <img
                        src={`https://flagicons.lipis.dev/flags/4x3/${flagCode}.svg`}
                        className="flex-none w-5 h-3.5 rounded-sm border border-gray-200"
                        alt={result.nationality}
                      />
                    ) : null}
                    <span className="text-gray-700">BIB {result.bibNumber || "-"}</span>
                    {genderSymbol && !isSelectedCategoryMix ? (
                      <span className={genderChar === "F" ? "text-pink-500" : "text-blue-500"}>
                        {genderSymbol}
                      </span>
                    ) : null}
                    {!isSelectedCategoryMix && (
                      <span className="ml-auto text-gray-500">
                        Gender Rank:{" "}
                        <span className="font-semibold text-gray-800">
                          {result.genderRanking || "-"}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden sm:block bg-white rounded-xl border border-gray-300 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 table-auto">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[50px]">
                  Rank
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[100px]">
                  Time
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[150px]">
                  Name
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bib
                </th>
                {selectedEvent !== "SMM" && (
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[150px]">
                    Country
                  </th>
                )}
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[150px]">
                  Gender
                </th>
                {!isSelectedCategoryMix && (
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gender Rank
                  </th>
                )}
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {state.loading ? (
                <SkeletonRow />
              ) : state.raceResults.length === 0 ? (
                <tr>
                  <td
                    colSpan={isSelectedCategoryMix ? (selectedEvent === "SMM" ? 6 : 7) : (selectedEvent === "SMM" ? 7 : 8)}
                    className="px-4 py-4 text-center text-sm text-gray-600"
                  >
                    No race results available for this category.
                  </td>
                </tr>
              ) : (
                state.raceResults.map((result, index) => (
                  <tr
                    key={`${result.id || "unknown"}-${index}`}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-2 py-4 text-sm text-gray-600 text-center w-[60px]">
                      {result.displayRanking || "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 font-bold">
                      {result.time || "-"}
                    </td>
                    <td
                      className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 uppercase max-w-[150px] truncate cursor-pointer hover:text-slate-900"
                      onClick={() => handleNameClick(result)}
                    >
                      {isSelectedCategoryMix
                        ? capitalizeWords(result.teamName)?.toUpperCase() || "-"
                        : capitalizeWords(
                            `${result.firstName} ${result.familyName || ""}`
                          )?.toUpperCase() || "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 text-center">
                      <span className="border border-gray-300 rounded-full px-2 py-1">
                        {result.bibNumber ? `BIB ${result.bibNumber}` : "-"}
                      </span>
                    </td>
                    {selectedEvent !== "SMM" && (
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 max-w-[150px] text-center">
                        <span className="border border-gray-300 rounded-full px-2 py-1 inline-flex items-center">
                          <img
                            src={`https://flagicons.lipis.dev/flags/4x3/${getFlagCode(
                              result.nationality
                            )}.svg`}
                            className="country-flag w-4 h-3 mr-1 border border-gray-300 rounded"
                            alt={result.nationality}
                          />
                          {result.nationality || "-"}
                        </span>
                      </td>
                    )}
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 max-w-[150px] text-center">
                      <span className="border border-gray-300 rounded-full px-2 py-1">
                        {formatGender(
                          result.gender,
                          isSelectedCategoryMix
                        )}
                      </span>
                    </td>
                    {!isSelectedCategoryMix && (
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 text-center">
                        {result.genderRanking || "-"}
                      </td>
                    )}
                    <td className="px-4 py-4 whitespace-nowrap text-xs text-green-700 text-center">
                      <span className="bg-green-50 border border-green-400 rounded-full px-2 py-1">
                        {result.time ? "Finished" : "DNF"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
            </>
          );
        })()}
      </div>
    </section>
  );
};
